import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance, {
  CheckUserExpiry,
  getUserDetails,
  isCompanyManagedBillingUser,
  isCompanyManagerUser,
  isFisherOwnerUser,
  isSuperUserSession,
} from "../../components/content/axiosConfig";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import { formatDisplayDate } from "../../utils/formatDisplayDate";
import { getScopedAccountId } from "../../utils/accountContext";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

function memberInitial(name) {
  const s = String(name || "").trim();
  return s ? s.charAt(0).toUpperCase() : "C";
}

const JoinConsultancy = () => {
  const [invites, setInvites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [respondingId, setRespondingId] = useState(null);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [requestingId, setRequestingId] = useState(null);
  const [managedBilling, setManagedBilling] = useState(false);
  const [managedName, setManagedName] = useState("a consultancy");

  const scopedFisherId = getScopedAccountId();
  const superScoped = isSuperUserSession() && scopedFisherId != null;

  const canUse = useMemo(() => {
    if (superScoped) return true;
    if (isCompanyManagerUser()) return false;
    if (isCompanyManagedBillingUser()) return false;
    return isFisherOwnerUser();
  }, [superScoped]);

  const fisherParams = useMemo(() => {
    if (superScoped) return { fisherId: scopedFisherId, accountId: scopedFisherId };
    return {};
  }, [superScoped, scopedFisherId]);

  const loadInvites = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get("api/fisher/company-invites", {
        params: fisherParams,
      });
      if (Array.isArray(data)) {
        setInvites(data);
        setManagedBilling(false);
      } else {
        setInvites(Array.isArray(data?.invites) ? data.invites : []);
        setManagedBilling(!!data?.isCompanyManagedBilling);
        if (data?.isCompanyManagedBilling) {
          try {
            const fisherRes = await axiosInstance.get(
              `api/fisher/GetFisher?id=${data.fisherId || scopedFisherId}`
            );
            setManagedName(fisherRes.data?.ManagedByCompanyName || "a consultancy");
          } catch {
            setManagedName("a consultancy");
          }
        }
      }
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to load invitations");
    }
  }, [fisherParams, scopedFisherId]);

  useEffect(() => {
    if (!canUse) {
      setIsLoading(false);
      return;
    }
    if (isCompanyManagedBillingUser() && !superScoped) {
      setManagedBilling(true);
      setManagedName(
        getUserDetails()?.ManagedByCompanyName ||
          getUserDetails()?.subscription?.ManagedByCompanyName ||
          "a consultancy"
      );
      setIsLoading(false);
      return;
    }
    (async () => {
      await CheckUserExpiry();
      await loadInvites();
      setIsLoading(false);
    })();
  }, [canUse, loadInvites, superScoped]);

  useEffect(() => {
    if (!canUse || managedBilling) return undefined;
    const q = searchQ.trim();
    if (q.length < 2) {
      setSearchResults([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axiosInstance.get("api/fisher/companies/lookup", {
          params: { q, ...fisherParams },
        });
        setSearchResults(Array.isArray(data) ? data : []);
      } catch (error) {
        showErrorToast(error.response?.data?.message || "Search failed");
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQ, canUse, managedBilling, fisherParams]);

  const requestJoin = async (company) => {
    if (requestingId != null) return;
    const result = await Swal.fire({
      title: `Request to join ${company.Name}?`,
      html: `<p class="text-start small mb-0">Package and access end date stay the same. After they accept, the consultancy manages renewals.</p>`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Send request",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#0d9488",
    });
    if (!result.isConfirmed) return;

    setRequestingId(company.id);
    try {
      const res = await axiosInstance.post("api/fisher/company-invites", {
        companyId: company.id,
        ...fisherParams,
      });
      showSuccessToast(res.data?.message || "Request sent");
      setSearchQ("");
      setSearchResults([]);
      await loadInvites();
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to send request");
    } finally {
      setRequestingId(null);
    }
  };

  const respond = async (invite, accept) => {
    if (respondingId != null) return;
    const companyName = invite.Company?.Name || "this consultancy";
    const isClaim = invite.InitiatedBy === "company";

    const result = await Swal.fire({
      title: accept
        ? `Join ${companyName}?`
        : isClaim
          ? `Decline ${companyName}?`
          : `Cancel request to ${companyName}?`,
      text: accept
        ? "Package and end date stay the same. They will handle renewals."
        : undefined,
      icon: accept ? "question" : "warning",
      showCancelButton: true,
      confirmButtonText: accept ? "Accept" : isClaim ? "Decline" : "Cancel request",
      cancelButtonText: "Back",
      confirmButtonColor: accept ? "#0d9488" : "#dc3545",
      focusCancel: true,
    });
    if (!result.isConfirmed) return;

    setRespondingId(invite.id);
    try {
      const res = await axiosInstance.post(
        `api/fisher/company-invites/${invite.id}/respond`,
        { accept: !!accept, ...fisherParams }
      );
      showSuccessToast(res.data?.message || "Updated");
      if (accept) {
        await CheckUserExpiry();
        if (superScoped) {
          window.location.href = "/dashboard";
        } else {
          window.location.href = "/fishingData";
        }
        return;
      }
      await loadInvites();
    } catch (error) {
      showErrorToast(error.response?.data?.message || "Failed to update invitation");
    } finally {
      setRespondingId(null);
    }
  };

  const renderGate = ({ icon, title, text, to, cta }) => (
    <div className="donezo-page donezo-list-page">
      <div className="container-fluid px-3 px-md-4 py-5">
        <div className="donezo-empty-state mx-auto" style={{ maxWidth: 440 }}>
          <div className="donezo-empty-state__icon" aria-hidden>
            <i className={`fa ${icon}`} />
          </div>
          <p className="donezo-empty-state__title">{title}</p>
          <p className="donezo-empty-state__text">{text}</p>
          <Link to={to} className="btn btn-primary rounded-pill px-4 mt-3">
            {cta}
          </Link>
        </div>
      </div>
    </div>
  );

  if (isLoading) return <Preloader />;

  if (isSuperUserSession() && !scopedFisherId) {
    return renderGate({
      icon: "fa-building",
      title: "Open a pond owner account first",
      text: "From Super Admin, open (scope into) a pond owner account, then use Join consultancy to accept claims or send join requests for that owner.",
      to: "/dashboard",
      cta: "Go to dashboard",
    });
  }

  if (managedBilling) {
    return renderGate({
      icon: "fa-link",
      title: `Managed by ${managedName}`,
      text: "This pond owner account is already linked to a consultancy.",
      to: superScoped ? "/dashboard" : "/fishingData",
      cta: superScoped ? "Back to dashboard" : "Go to sampling",
    });
  }

  if (!canUse) {
    return renderGate({
      icon: "fa-lock",
      title: "Pond owner account required",
      text: "Only independent pond owners can join a consultancy.",
      to: "/pond",
      cta: "Go to ponds",
    });
  }

  const renderInviteActions = (inv, alwaysVisible = false) => {
    const isClaim = inv.InitiatedBy === "company";
    const busy = respondingId === inv.id;
    return (
      <div className={`donezo-orders-actions${alwaysVisible ? " is-visible" : ""}`}>
        {isClaim ? (
          <>
            <button
              type="button"
              className="donezo-orders-actions__btn"
              title="Accept"
              aria-label="Accept claim"
              disabled={busy}
              onClick={() => respond(inv, true)}
            >
              <i className="fa fa-check" aria-hidden />
            </button>
            <button
              type="button"
              className="donezo-orders-actions__btn donezo-orders-actions__btn--danger"
              title="Decline"
              aria-label="Decline claim"
              disabled={busy}
              onClick={() => respond(inv, false)}
            >
              <i className="fa fa-times" aria-hidden />
            </button>
          </>
        ) : (
          <button
            type="button"
            className="donezo-orders-actions__btn donezo-orders-actions__btn--danger"
            title="Cancel request"
            aria-label="Cancel request"
            disabled={busy}
            onClick={() => respond(inv, false)}
          >
            <i className="fa fa-times" aria-hidden />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="donezo-page donezo-list-page donezo-orders-page donezo-upload-page">
      <div className="container-fluid px-3 px-md-4 py-3 py-md-4">
        <div className="donezo-orders-toolbar donezo-upload-toolbar">
          <div className="donezo-upload-toolbar__intro">
            <h1 className="donezo-orders-toolbar__title">Join a consultancy</h1>
            <p className="donezo-upload-toolbar__subtitle">
              Request to join a Pond Management Company, or accept a claim they sent you. Package
              and end date stay the same; they handle renewals after linking.
            </p>
          </div>
        </div>

        <div className="donezo-orders-panel donezo-upload-section mb-3">
          <div className="donezo-upload-section__header">
            <i className="fa fa-search" aria-hidden />
            <h2 className="donezo-upload-section__title">Find a consultancy</h2>
          </div>
          <div className="donezo-upload-section__body">
            <div className="donezo-invite-search">
              <label htmlFor="join-consultancy-search" className="visually-hidden">
                Search consultancies
              </label>
              <div className="donezo-orders-toolbar__search donezo-invite-search__field">
                <i className="fa fa-building" aria-hidden />
                <input
                  id="join-consultancy-search"
                  type="search"
                  className="form-control"
                  placeholder="Search by company name or email…"
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>

            {searching ? (
              <p className="donezo-upload-section__hint mb-0 mt-3">
                <i className="fa fa-spinner fa-spin me-1" aria-hidden /> Searching…
              </p>
            ) : null}

            {!searching && searchQ.trim().length >= 2 && searchResults.length === 0 ? (
              <div className="donezo-upload-notice donezo-upload-notice--warn mt-3 mb-0">
                No companies found for that search.
              </div>
            ) : null}

            {searchResults.length > 0 ? (
              <ul className="donezo-join-results mt-3">
                {searchResults.map((c) => (
                  <li key={c.id} className="donezo-join-results__item">
                    <div className="donezo-orders-pond">
                      <span className="donezo-orders-pond__avatar" aria-hidden>
                        {memberInitial(c.Name)}
                      </span>
                      <div className="donezo-orders-pond__meta">
                        <span className="donezo-orders-pond__name">{c.Name}</span>
                        <span className="donezo-team-email" title={c.Email || undefined}>
                          {c.Email || "—"}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="donezo-orders-toolbar__ghost"
                      disabled={requestingId === c.id}
                      onClick={() => requestJoin(c)}
                    >
                      {requestingId === c.id ? "Sending…" : "Request to join"}
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}

            {!searching && searchQ.trim().length < 2 && searchResults.length === 0 ? (
              <p className="donezo-upload-section__hint mb-0 mt-3">
                Type at least 2 characters to search.
              </p>
            ) : null}
          </div>
        </div>

        <div className="donezo-orders-panel donezo-upload-section">
          <div className="donezo-upload-section__header donezo-upload-section__header--split">
            <div className="d-flex align-items-center gap-2">
              <i className="fa fa-inbox" aria-hidden />
              <h2 className="donezo-upload-section__title">Pending</h2>
            </div>
            <span className="donezo-upload-section__meta">
              {invites.length} {invites.length === 1 ? "entry" : "entries"}
            </span>
          </div>

          {invites.length === 0 ? (
            <div className="donezo-empty-state donezo-empty-state--compact">
              <div className="donezo-empty-state__icon" aria-hidden>
                <i className="fa fa-inbox" />
              </div>
              <p className="donezo-empty-state__title">No pending items</p>
              <p className="donezo-empty-state__text">
                No invitations or join requests right now.
              </p>
            </div>
          ) : (
            <>
              <div className="d-none d-md-block table-responsive">
                <table className="table align-middle mb-0 donezo-orders-table">
                  <thead>
                    <tr>
                      <th>Consultancy</th>
                      <th>Type</th>
                      <th>Sent</th>
                      <th className="donezo-orders-table__actions-col" aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {invites.map((inv) => {
                      const isClaim = inv.InitiatedBy === "company";
                      const company = inv.Company || {};
                      return (
                        <tr key={inv.id}>
                          <td>
                            <div className="donezo-orders-pond">
                              <span className="donezo-orders-pond__avatar" aria-hidden>
                                {memberInitial(company.Name)}
                              </span>
                              <div className="donezo-orders-pond__meta">
                                <span className="donezo-orders-pond__name">
                                  {company.Name || "Consultancy"}
                                </span>
                                <span
                                  className="donezo-team-email"
                                  title={company.Email || undefined}
                                >
                                  {company.Email || (inv.Message ? inv.Message : "—")}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span
                              className={`donezo-status-dot donezo-status-dot--${
                                isClaim ? "natural" : "river"
                              }`}
                            >
                              <span className="donezo-status-dot__mark" aria-hidden />
                              {isClaim ? "Claim" : "Your request"}
                            </span>
                          </td>
                          <td className="donezo-orders-table__date">
                            {inv.createdAt ? formatDisplayDate(inv.createdAt) : "—"}
                          </td>
                          <td className="donezo-orders-table__actions-col">
                            {renderInviteActions(inv, true)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="d-md-none donezo-orders-panel__mobile">
                {invites.map((inv) => {
                  const isClaim = inv.InitiatedBy === "company";
                  const company = inv.Company || {};
                  return (
                    <div key={inv.id} className="donezo-mobile-card">
                      <div className="donezo-mobile-card__head">
                        <div className="donezo-orders-pond">
                          <span className="donezo-orders-pond__avatar" aria-hidden>
                            {memberInitial(company.Name)}
                          </span>
                          <div className="donezo-orders-pond__meta">
                            <h2 className="donezo-mobile-card__title mb-0">
                              {company.Name || "Consultancy"}
                            </h2>
                            <span className="donezo-team-email">{company.Email || "—"}</span>
                          </div>
                        </div>
                        {renderInviteActions(inv, true)}
                      </div>
                      <div className="donezo-mobile-card__grid">
                        <div>
                          <span className="donezo-mobile-card__label">Type</span>
                          <div className="donezo-mobile-card__value">
                            <span
                              className={`donezo-status-dot donezo-status-dot--${
                                isClaim ? "natural" : "river"
                              }`}
                            >
                              <span className="donezo-status-dot__mark" aria-hidden />
                              {isClaim ? "Claim" : "Your request"}
                            </span>
                          </div>
                        </div>
                        <div>
                          <span className="donezo-mobile-card__label">Sent</span>
                          <div className="donezo-mobile-card__value donezo-orders-table__date">
                            {inv.createdAt ? formatDisplayDate(inv.createdAt) : "—"}
                          </div>
                        </div>
                        {inv.Message ? (
                          <div className="donezo-mobile-card__full">
                            <span className="donezo-mobile-card__label">Message</span>
                            <div className="donezo-mobile-card__value">{inv.Message}</div>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default JoinConsultancy;
